import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react"

import type {
  FoundationConsoleSessionV1,
  FoundationMutationResponseV1,
  FoundationProjectionReadV1,
  MembershipPersonaV1,
  TenantChoiceV1,
} from "@impetusos/contracts"
import {
  contractorPaymentResultV1Schema,
  correlationIdSchema,
  employeePayrollResultV1Schema,
  payrollExternalExportRowV1Schema,
  payrollReconciliationItemV1Schema,
} from "@impetusos/contracts"

import {
  stateLabel,
  stateTone,
  stateVersion,
  syntheticCustomerWorkspace,
} from "../foundations/index.js"
import {
  ConsoleRequestError,
  mutateFoundation,
  readFoundation,
  readSession,
  signIn,
  signOut,
  switchTenant,
} from "../transport/index.js"
import { InternalCustomerWizard } from "./InternalCustomerWizard.js"
import { CustomerTypeProfileEditor } from "./CustomerTypeProfileEditor.js"
import { CustomerWorkspace } from "./CustomerWorkspace.js"
import { CustomerToCashWorkspace } from "./CustomerToCashWorkspace.js"
import { FinanceWorkspace } from "./FinanceWorkspace.js"
import { CustomerJourneyWorkspace } from "./CustomerJourneyWorkspace.js"
import { PropertyMeasurementWorkspace } from "./PropertyMeasurementWorkspace.js"
import { DispatchBoard } from "./DispatchBoard.js"
import { SupportCaseBoard } from "./SupportCaseBoard.js"
import { OperationsDashboard } from "./OperationsDashboard.js"
import { EmployeePayrollResultPanel } from "./EmployeePayrollResultPanel.js"
import { ContractorPaymentResultPanel } from "./ContractorPaymentResultPanel.js"
import { PayrollReconciliationWorkspace } from "./PayrollReconciliationWorkspace.js"
import { PayrollExternalExportPanel } from "./PayrollExternalExportPanel.js"
import { PayrollLateResultNotice } from "./PayrollLateResultNotice.js"
import { InteractionState } from "./InteractionState.js"
import { ImpetusBrandMark } from "./ImpetusBrandMark.js"
import {
  defaultTenantRoute,
  tenantNavigation,
  tenantRouteFromHash,
  type TenantNavigationIcon,
  type TenantRouteId,
} from "./navigation.js"

type LoadState = "loading" | "ready" | "denied" | "retry"

const developmentRoles = [
  { value: "administrator", label: "Owner / administrator" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "office_staff", label: "Office staff" },
  { value: "accountant", label: "Accountant" },
  { value: "field_worker", label: "Worker" },
  { value: "contractor", label: "Contractor" },
] as const satisfies readonly {
  value: MembershipPersonaV1
  label: string
}[]

const personaLabel = (persona: MembershipPersonaV1) =>
  developmentRoles.find((role) => role.value === persona)?.label ?? "Tenant member"

const FoundationIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 19h16M6.5 19v-8.5L12 5l5.5 5.5V19M9.5 19v-5h5v5" />
  </svg>
)

const PulseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 12h4l2-5 4 10 2-5h6" />
  </svg>
)

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6l-7-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
)

const NavigationIcon = ({ icon }: { icon: TenantNavigationIcon }) => {
  if (icon === "customers" || icon === "support") return <ShieldIcon />
  if (icon === "map") return <FoundationIcon />
  return <PulseIcon />
}

const subscribeToTenantRoute = (notify: () => void) => {
  window.addEventListener("hashchange", notify)
  return () => window.removeEventListener("hashchange", notify)
}

const selectedTenant = (session: FoundationConsoleSessionV1): TenantChoiceV1 => {
  const tenant = session.tenants.find((candidate) =>
    candidate.membershipId === session.selectedMembershipId &&
    candidate.tenantId === session.selectedTenantId
  )
  if (tenant === undefined) throw new Error("selected_tenant_missing")
  return tenant
}

const Login = ({ busy, error, persona, onPersona, onLogin }: {
  busy: boolean
  error: string | null
  persona: MembershipPersonaV1
  onPersona: (persona: MembershipPersonaV1) => void
  onLogin: () => void
}) => (
  <main className="login-shell">
    <section className="login-story" aria-labelledby="login-title">
      <div className="wordmark wordmark-light"><ImpetusBrandMark /><span>ImpetusOS</span></div>
      <div className="story-copy">
        <p className="eyebrow eyebrow-light">Foundation workspace</p>
        <h1 id="login-title">A calm place to run the work.</h1>
        <p>One operational view, with every action anchored to the right Tenant and confirmed by the source.</p>
      </div>
      <div className="story-proof">
        <span className="proof-line" />
        <p>Tenant-scoped · Human-attributed · Frappe-authoritative</p>
      </div>
    </section>
    <section className="login-panel" aria-label="Sign in">
      <div className="login-card">
        <p className="eyebrow">Development access</p>
        <h2>Welcome back</h2>
        <p className="muted">Choose an authorized sandbox identity to verify its exact Membership experience.</p>
        <label className="login-role-picker">
          <span>Sandbox identity</span>
          <select
            value={persona}
            onChange={(event) => onPersona(event.target.value as MembershipPersonaV1)}
          >
            {developmentRoles.map((role) => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
        </label>
        {error && <div className="inline-alert" role="alert">{error}</div>}
        <button className="primary-button full-button" type="button" onClick={onLogin} disabled={busy}>
          <span>{busy ? "Signing in…" : "Sign in to ImpetusOS"}</span>
          <span aria-hidden="true">→</span>
        </button>
        <p className="privacy-note"><ShieldIcon /> Credentials and access tokens never enter the browser UI.</p>
      </div>
    </section>
  </main>
)

const Workspace = ({ session, onSessionChange, onSessionExpired }: {
  session: FoundationConsoleSessionV1
  onSessionChange: (session: FoundationConsoleSessionV1) => void
  onSessionExpired: () => void
}) => {
  const [projection, setProjection] = useState<FoundationProjectionReadV1 | null>(null)
  const [loadState, setLoadState] = useState<LoadState>("loading")
  const [actionPending, setActionPending] = useState(false)
  const [tenantPending, setTenantPending] = useState(false)
  const [receipt, setReceipt] = useState<FoundationMutationResponseV1 | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState(
    session.selectedLegalEntityId
  )
  const [selectedBranchId, setSelectedBranchId] = useState(session.selectedBranchId)
  const activeRoute = useSyncExternalStore(
    subscribeToTenantRoute,
    () => tenantRouteFromHash(window.location.hash)
  )
  const tenant = useMemo(() => selectedTenant(session), [session])
  const capabilitySet = useMemo(
    () => new Set(session.capabilities),
    [session.capabilities]
  )
  const visibleNavigation = useMemo(
    () => tenantNavigation.filter((item) =>
      item.requiredCapabilities.every((capability) =>
        capabilitySet.has(capability)
      )
    ),
    [capabilitySet]
  )
  useEffect(() => {
    const firstAuthorizedRoute = visibleNavigation[0]
    if (window.location.hash.length === 0 && firstAuthorizedRoute !== undefined) {
      window.location.hash = firstAuthorizedRoute.href.slice(1)
    }
  }, [visibleNavigation])
  const routeAuthorized = visibleNavigation.some((item) => item.id === activeRoute.id)
  const visibleBranches = session.branches.filter((branch) =>
    selectedLegalEntityId === null || branch.legalEntityId === selectedLegalEntityId
  )
  const showDevelopmentProofs = new URLSearchParams(window.location.search)
    .get("developmentProofs") === "true"
  const customer = useMemo(() => syntheticCustomerWorkspace(), [])
  const payrollResult = useMemo(() => employeePayrollResultV1Schema.parse({
    entityType: "employee_payroll_result",
    entityId: "018f3c21-6a45-7a89-8abc-123456789051",
    state: "settled",
    version: 1,
    accessEventId: "018f3c21-6a45-7a89-8abc-123456789052",
    runId: "018f3c21-6a45-7a89-8abc-123456789053",
    workerId: "018f3c21-6a45-7a89-8abc-123456789054",
    payPeriodStart: "2026-07-01",
    payPeriodEnd: "2026-07-15",
    payDate: "2026-07-18",
    currency: "USD",
    grossAmount: "2400.00",
    taxAmount: "420.00",
    deductionAmount: "180.00",
    netAmount: "1800.00",
    documentStatus: "available",
    authoritativeSource: "Worklio",
    recalculated: false,
    accessAudited: true,
    downloadAuthorized: false,
  }), [])
  const contractorResult = useMemo(() => contractorPaymentResultV1Schema.parse({
    entityType: "contractor_payment_result",
    entityId: "018f3c21-6a45-7a89-8abc-123456789061",
    state: "settled",
    version: 1,
    accessEventId: "018f3c21-6a45-7a89-8abc-123456789062",
    runId: "018f3c21-6a45-7a89-8abc-123456789063",
    payrollInputId: "018f3c21-6a45-7a89-8abc-123456789064",
    workerId: "018f3c21-6a45-7a89-8abc-123456789065",
    payableKind: "contractor_job",
    approvedMinutes: null,
    approvedAmount: "125.00",
    payPeriodStart: "2026-07-01",
    payPeriodEnd: "2026-07-15",
    payDate: "2026-07-18",
    currency: "USD",
    providerGrossAmount: "125.00",
    providerNetAmount: "125.00",
    form1099Status: "available",
    form1099Year: 2026,
    authoritativeSource: "Worklio",
    recalculated: false,
    accessAudited: true,
    downloadAuthorized: false,
  }), [])
  const reconciliationItems = useMemo(() => [
    ["input", "approved_input_changed"],
    ["run", "provider_run_changed"],
    ["settlement", "settlement_status_changed"],
    ["accounting", "journal_total_changed"],
    ["document", "document_fingerprint_changed"],
  ].map(([mismatchKind, reasonCode], index) =>
    payrollReconciliationItemV1Schema.parse({
      exceptionId: `018f3c21-6a45-7a89-8abc-12345678907${index}`,
      legalEntityId: "018f3c21-6a45-7a89-8abc-123456789071",
      branchId: "018f3c21-6a45-7a89-8abc-123456789072",
      runId: "018f3c21-6a45-7a89-8abc-123456789053",
      mismatchKind,
      reasonCode,
      expectedRef: "a".repeat(16),
      observedRef: "b".repeat(16),
      state: "open",
      assignedMembershipId: null,
      retryCount: 0,
      resolutionCode: null,
      detectedAt: "2026-07-27T12:00:00Z",
      updatedAt: "2026-07-27T12:00:00Z",
      version: 1,
    })
  ), [])
  const externalExportRows = useMemo(() => [
    payrollExternalExportRowV1Schema.parse({
      inputId: "018f3c21-6a45-7a89-8abc-123456789081",
      inputVersion: 2,
      workShiftId: "018f3c21-6a45-7a89-8abc-123456789082",
      sourceShiftVersion: 1,
      workerId: "018f3c21-6a45-7a89-8abc-123456789083",
      relationshipId: "018f3c21-6a45-7a89-8abc-123456789084",
      kind: "employee_time",
      minutes: 480,
      jobReference: null,
      amountMinor: null,
      currency: null,
    }),
  ], [])

  const loadFoundation = useCallback(async () => {
    setLoadState("loading")
    setNotice(null)
    try {
      setProjection(await readFoundation())
      setLoadState("ready")
    } catch (error) {
      if (error instanceof ConsoleRequestError && error.kind === "denied") {
        setProjection(null)
        setLoadState("denied")
      } else {
        setLoadState("retry")
      }
    }
  }, [])

  useEffect(() => {
    if (window.location.hash.length === 0) {
      window.history.replaceState(null, "", defaultTenantRoute.href)
    }
  }, [])

  useEffect(() => {
    setSelectedLegalEntityId(session.selectedLegalEntityId)
    setSelectedBranchId(session.selectedBranchId)
  }, [session])

  useEffect(() => { void loadFoundation() }, [loadFoundation, session.selectedTenantId])

  const handleTenant = async (choice: string) => {
    const next = session.tenants.find((candidate) => candidate.membershipId === choice)
    if (next === undefined || next.membershipId === session.selectedMembershipId) return
    setTenantPending(true)
    setReceipt(null)
    try {
      onSessionChange(await switchTenant(next.membershipId, next.tenantId))
    } catch {
      setNotice("Tenant switch could not be completed. Please try again.")
    } finally {
      setTenantPending(false)
    }
  }

  const handleMutation = async () => {
    const version = stateVersion(projection)
    const targetState = projection?.found && projection.record.state === "active" ? "paused" : "active"
    setActionPending(true)
    setNotice(null)
    setReceipt(null)
    try {
      const result = await mutateFoundation({
        correlationId: correlationIdSchema.parse(crypto.randomUUID()),
        idempotencyKey: `foundation-ui-${crypto.randomUUID()}`,
        expectedVersion: version,
        targetState,
      })
      setReceipt(result)
      if (result.projection !== null) setProjection(result.projection)
      if (result.status === "conflict") {
        setNotice("This record changed before your action completed. Current state has been refreshed.")
      } else if (result.status === "denied") {
        setNotice("Your current Membership no longer allows this action.")
      } else if (result.status === "retry") {
        setNotice("The authoritative service did not confirm the action. Nothing is shown as complete—please retry.")
      }
    } catch (error) {
      if (error instanceof ConsoleRequestError && error.kind === "denied") {
        setNotice("Your session or Membership is no longer active. Sign in again.")
      } else {
        setNotice("The authoritative service did not confirm the action. Please retry.")
      }
    } finally {
      setActionPending(false)
    }
  }

  const handleSignOut = async () => {
    try { await signOut() } finally { onSessionExpired() }
  }

  const label = stateLabel(projection)
  const version = stateVersion(projection)
  const tone = stateTone(projection)
  const targetLabel = projection?.found && projection.record.state === "active"
    ? "Pause foundation"
    : "Activate foundation"

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="wordmark"><ImpetusBrandMark /><span>ImpetusOS</span></div>
        <nav aria-label="Primary navigation">
          {visibleNavigation.map((item) => (
            <a
              key={item.id}
              href={item.href}
              className={`nav-item${activeRoute.id === item.id ? " active" : ""}`}
              aria-current={activeRoute.id === item.id ? "page" : undefined}
            >
              <NavigationIcon icon={item.icon} />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="avatar" aria-hidden="true">JE</div>
          <div>
            <strong>{session.displayName}</strong>
            <span>{personaLabel(session.persona)}</span>
          </div>
          <button type="button" className="text-button" onClick={handleSignOut}>Sign out</button>
        </div>
      </aside>

      <main className="workspace" id="workspace">
        <header className="workspace-header">
          <div>
            <p className="eyebrow">{activeRoute.eyebrow}</p>
            <h1>{activeRoute.title}</h1>
          </div>
          <label className="mobile-route-picker">
            <span>Workspace</span>
            <select
              value={activeRoute.id}
              onChange={(event) => {
                const route = tenantNavigation.find(
                  (item) => item.id === event.target.value as TenantRouteId
                )
                if (route !== undefined) window.location.hash = route.href.slice(1)
              }}
            >
              {!routeAuthorized && (
                <option value={activeRoute.id}>Access unavailable</option>
              )}
              {visibleNavigation.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>
          <label className="scope-picker">
            <span>Legal Entity</span>
            <select
              value={selectedLegalEntityId ?? ""}
              onChange={(event) => {
                const legalEntityId = session.legalEntities.find(
                  (choice) => choice.legalEntityId === event.target.value
                )?.legalEntityId ?? null
                const firstBranch = session.branches.find(
                  (branch) => branch.legalEntityId === legalEntityId
                )
                setSelectedLegalEntityId(legalEntityId)
                setSelectedBranchId(firstBranch?.branchId ?? null)
              }}
            >
              {session.legalEntities.map((choice) => (
                <option key={choice.legalEntityId} value={choice.legalEntityId}>
                  {choice.name}
                </option>
              ))}
            </select>
          </label>
          <label className="scope-picker">
            <span>Branch</span>
            <select
              value={selectedBranchId ?? ""}
              onChange={(event) => setSelectedBranchId(
                session.branches.find(
                  (choice) => choice.branchId === event.target.value
                )?.branchId ?? null
              )}
            >
              {visibleBranches.map((choice) => (
                <option key={choice.branchId} value={choice.branchId}>{choice.name}</option>
              ))}
            </select>
          </label>
          <label className="tenant-picker">
            <span>Active Tenant</span>
            <select
              value={session.selectedMembershipId}
              onChange={(event) => { void handleTenant(event.target.value) }}
              disabled={tenantPending}
            >
              {session.tenants.map((choice) => (
                <option key={choice.membershipId} value={choice.membershipId}>
                  {choice.name}{choice.available ? "" : " — unavailable"}
                </option>
              ))}
            </select>
          </label>
        </header>

        <section className="tenant-context" aria-label="Selected Tenant">
          <div className="tenant-monogram" aria-hidden="true">{tenant.name.slice(0, 1)}</div>
          <div><strong>{tenant.name}</strong><span>{tenant.location}</span></div>
          <span className={`availability ${tenant.available ? "online" : "offline"}`}>
            <i />{tenant.available ? "Authoritative site available" : "Site unavailable"}
          </span>
          <span className="permission-version">
            Membership permission version {session.permissionVersion}
          </span>
        </section>

        {notice && <div className="notice" role="status"><span aria-hidden="true">!</span><p>{notice}</p></div>}

        {!routeAuthorized && (
          <InteractionState
            tone="denied"
            title="This workspace is not available"
            detail="Your current Membership does not grant this capability. No other Tenant or Branch was searched."
          />
        )}
        {routeAuthorized && activeRoute.id === "customer-intake" && <InternalCustomerWizard />}
        {routeAuthorized && activeRoute.id === "customer-profiles" && <CustomerTypeProfileEditor />}
        {routeAuthorized && activeRoute.id === "property-mapping" && <PropertyMeasurementWorkspace />}
        {routeAuthorized && activeRoute.id === "dispatch" && <DispatchBoard />}
        {routeAuthorized && activeRoute.id === "finance" && (
          selectedLegalEntityId
            ? <FinanceWorkspace
                legalEntityId={selectedLegalEntityId}
                legalEntityName={session.legalEntities.find(
                  (item) => item.legalEntityId === selectedLegalEntityId,
                )?.name ?? "Selected Legal Entity"}
                branchId={selectedBranchId}
                branchName={session.branches.find(
                  (item) => item.branchId === selectedBranchId,
                )?.name ?? null}
                capabilities={session.capabilities}
              />
            : <InteractionState
                tone="empty"
                title="Select a Legal Entity"
                detail="Choose an available set of books before opening Finance."
              />
        )}
        {routeAuthorized && activeRoute.id === "customer-to-cash" && (
          <>
            {selectedLegalEntityId && selectedBranchId && (
              <CustomerJourneyWorkspace
                persona={session.persona}
                legalEntityId={selectedLegalEntityId}
                branchId={selectedBranchId}
              />
            )}
            <CustomerToCashWorkspace />
          </>
        )}
        {routeAuthorized && activeRoute.id === "sandbox-work" && (
          selectedLegalEntityId && selectedBranchId
            ? (
                <CustomerJourneyWorkspace
                  persona={session.persona}
                  legalEntityId={selectedLegalEntityId}
                  branchId={selectedBranchId}
                />
              )
            : (
                <InteractionState
                  tone="denied"
                  title="Select a work scope"
                  detail="Choose an available Legal Entity and Branch before opening sandbox work."
                />
              )
        )}
        {routeAuthorized && activeRoute.id === "support" && <SupportCaseBoard />}
        {routeAuthorized && activeRoute.id === "operations" && <OperationsDashboard />}
        {showDevelopmentProofs && routeAuthorized && activeRoute.id === "foundation" && (
          <section className="development-proofs" aria-label="Development-only presentation proofs">
            <div className="development-proof-banner">
              Development-only synthetic presentation models. These are excluded from normal navigation.
            </div>
            <CustomerWorkspace model={customer} />
            <EmployeePayrollResultPanel
          status="ready"
          result={payrollResult}
          employeeLabel="Synthetic employee"
          onRetry={() => undefined}
          onDownload={() => {
            setNotice("A fresh audited salary-slip download was requested.")
          }}
            />
            <ContractorPaymentResultPanel
          status="ready"
          result={contractorResult}
          contractorLabel="Synthetic contractor"
          onRetry={() => undefined}
          onDownload1099={() => {
            setNotice("A fresh audited contractor 1099 download was requested.")
          }}
            />
            <PayrollReconciliationWorkspace
          status="ready"
          items={reconciliationItems}
          onRetry={() => undefined}
          onAssign={(item) => {
            setNotice(`Assignment requested for ${item.mismatchKind} mismatch.`)
          }}
          onRequestRetry={(item) => {
            setNotice(`Audited retry requested for ${item.mismatchKind} mismatch.`)
          }}
          onResolve={(item) => {
            setNotice(`Controlled resolution requested for ${item.mismatchKind} mismatch.`)
          }}
            />
            <PayrollExternalExportPanel
          status="ready"
          reasonCode="unsupported_state"
          rows={externalExportRows}
          onRetry={() => undefined}
          onExport={() => {
            setNotice("A versioned external payroll CSV was requested.")
          }}
            />
            <PayrollLateResultNotice
          status="ready"
          originalPostingDate="2026-06-30"
          adjustmentPostingDate="2026-07-27"
          onRetry={() => undefined}
            />
          </section>
        )}

        {routeAuthorized && activeRoute.id === "foundation" && <div className="dashboard-grid" id="foundation">
          <section className="state-card" aria-labelledby="state-heading">
            <div className="card-heading">
              <div>
                <p className="eyebrow">Authoritative state</p>
                <h2 id="state-heading">Foundation record</h2>
              </div>
              {loadState === "ready" && (
                <span className={`freshness ${projection?.freshness.status ?? "stale"}`}>
                  <i />{projection?.freshness.status === "fresh" ? "Confirmed current" : "Stale read"}
                </span>
              )}
            </div>

            {loadState === "loading" && (
              <div className="loading-state" aria-live="polite">
                <span className="spinner" /><div><strong>Checking Frappe</strong><p>Reading the selected Tenant’s source of truth.</p></div>
              </div>
            )}
            {loadState === "denied" && (
              <div className="empty-state denied-state">
                <span className="state-symbol">×</span>
                <h3>Access unavailable</h3>
                <p>This Tenant has no routed development site. No other Tenant’s data was searched or shown.</p>
              </div>
            )}
            {loadState === "retry" && (
              <div className="empty-state">
                <span className="state-symbol">↻</span>
                <h3>Couldn’t confirm current state</h3>
                <p>Nothing has been changed. Retry when the authoritative service is available.</p>
                <button type="button" className="secondary-button" onClick={() => { void loadFoundation() }}>Retry read</button>
              </div>
            )}
            {loadState === "ready" && (
              <>
                <div className="state-reading">
                  <div className={`state-orb ${tone}`}><span>{version.toString().padStart(2, "0")}</span></div>
                  <div>
                    <span className="field-label">Current state</span>
                    <p className="state-value">{label}</p>
                    <span className="version-label">Version {version}</span>
                  </div>
                </div>
                <div className="action-block">
                  <div>
                    <strong>{targetLabel}</strong>
                    <p>Uses expected version {version}; Frappe revalidates the current Membership before commit.</p>
                  </div>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => { void handleMutation() }}
                    disabled={actionPending || projection?.freshness.status === "stale"}
                  >
                    {actionPending ? "Awaiting confirmation…" : targetLabel}
                  </button>
                </div>
              </>
            )}
          </section>

          <aside className="side-stack">
            <section className="receipt-card" aria-labelledby="receipt-heading">
              <div className="card-heading compact">
                <div><p className="eyebrow">Last action</p><h2 id="receipt-heading">Audit receipt</h2></div>
                <ShieldIcon />
              </div>
              {receipt?.status === "confirmed" ? (
                <div className="receipt-body confirmed-receipt">
                  <div className="check-badge">✓</div>
                  <strong>Recorded atomically</strong>
                  <p>State, idempotency, human audit, and outbox were committed together.</p>
                  <dl>
                    <div><dt>Audit</dt><dd>{receipt.auditStatus}</dd></div>
                    <div><dt>Correlation</dt><dd title={receipt.correlationId}>{receipt.correlationId.slice(0, 8)}…</dd></div>
                  </dl>
                </div>
              ) : (
                <div className="receipt-body empty-receipt">
                  <span className="receipt-glyph">⌁</span>
                  <p>Your next confirmed action will appear here with its correlation receipt.</p>
                </div>
              )}
            </section>

            <section className="health-card" id="health" aria-labelledby="health-heading">
              <div><p className="eyebrow">Connection</p><h2 id="health-heading">Trust path</h2></div>
              <ol className="trust-path">
                <li><span>1</span><div><strong>Membership scoped</strong><p>{tenant.name}</p></div></li>
                <li><span>2</span><div><strong>BFF signed</strong><p>Human actor + correlation</p></div></li>
                <li><span>3</span><div><strong>Frappe authoritative</strong><p>Versioned commit boundary</p></div></li>
              </ol>
            </section>
          </aside>
        </div>}
      </main>
    </div>
  )
}

const StaffApp = () => {
  const [session, setSession] = useState<FoundationConsoleSessionV1 | null | undefined>()
  const [loginBusy, setLoginBusy] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [persona, setPersona] = useState<MembershipPersonaV1>("administrator")

  useEffect(() => {
    readSession().then(setSession).catch(() => {
      setSession(null)
      setLoginError("The workspace is not available yet. Please retry.")
    })
  }, [])

  const handleLogin = async () => {
    setLoginBusy(true)
    setLoginError(null)
    try {
      setSession(await signIn(persona))
    } catch {
      setLoginError("Sign-in could not be completed. Please retry.")
    } finally {
      setLoginBusy(false)
    }
  }

  if (session === undefined) return (
    <main className="boot-screen">
      <ImpetusBrandMark />
      <InteractionState
        tone="loading"
        title="Opening workspace"
        detail="Confirming your current session and Tenant access."
      />
    </main>
  )
  if (session === null) return (
    <Login
      busy={loginBusy}
      error={loginError}
      persona={persona}
      onPersona={setPersona}
      onLogin={() => { void handleLogin() }}
    />
  )
  return <Workspace session={session} onSessionChange={setSession} onSessionExpired={() => setSession(null)} />
}

export const App = () => {
  if (
    window.location.pathname.startsWith("/portal")
    || window.location.pathname.startsWith("/secure")
  ) {
    return (
      <main className="portal-boundary">
        <p className="eyebrow">Secure customer access</p>
        <h1>A current secure link is required.</h1>
        <p>No Customer, Estimate, Invoice, or payment data is shown without an authoritative access grant.</p>
        <a href="/">Return to ImpetusOS</a>
      </main>
    )
  }
  if (window.location.pathname.startsWith("/payments/complete")) {
    return (
      <main className="portal-boundary">
        <p className="eyebrow">Stripe test payment</p>
        <h1>Payment submitted.</h1>
        <p>Return to the customer-to-cash workspace and refresh the receipt and statement while the verified webhook is processed.</p>
        <a href="/#/finance/customer-to-cash">Return to customer-to-cash</a>
      </main>
    )
  }
  if (window.location.pathname.startsWith("/payments/canceled")) {
    return (
      <main className="portal-boundary">
        <p className="eyebrow">Stripe test payment</p>
        <h1>Payment was not completed.</h1>
        <p>No payment is reported as successful. The existing checkout can be reopened from the customer-to-cash workspace.</p>
        <a href="/#/finance/customer-to-cash">Return to customer-to-cash</a>
      </main>
    )
  }
  return <StaffApp />
}
